<template>
  <section class="student-case-process" aria-label="案件处置">
    <div class="student-case-process-heading">
      <b>案件处置</b>
      <span v-if="h.selectedCase.flowStep === 7" class="student-case-process-done">已结案</span>
    </div>

    <div class="student-case-identity">
      <span>办理身份</span>
      <button class="btn small" :class="{ primary: h.run.currentIdentity === 'member' }" :disabled="!h.canOperate || h.busy" @click="h.changeIdentity('member')">网格员</button>
      <button class="btn small" :class="{ primary: h.run.currentIdentity === 'commander' }" :disabled="!h.canOperate || h.busy" @click="h.changeIdentity('commander')">指挥者</button>
    </div>

    <div class="lab-flow lab-case-flow">
      <template v-for="(step, index) in h.reference.flow">
        <span :key="'step-' + index" class="lab-step" :class="{ done: index < h.selectedCase.flowStep, cur: index === h.selectedCase.flowStep }">{{ index + 1 }}. {{ step.name }} <small>[{{ step.actor }}]</small></span>
        <span v-if="index < 6" :key="'arrow-' + index" class="lab-arrow">→</span>
      </template>
    </div>

    <div v-if="h.selectedCase.flowStep === 7" class="lab-ok">案件已结案 · 满意度 {{ h.selectedCase.satisfaction }} 分 · {{ h.formatTime(h.selectedCase.closeAt) }}</div>
    <div v-else-if="h.selectedCase.status === 'NEEDS_INFO'" class="lab-warning">
      <p>资料不足，请由网格员补充材料。</p>
      <label class="lab-field">补充材料<textarea v-model="h.operationNote" rows="3"></textarea></label>
      <button class="btn small primary" :disabled="!h.canOperate || h.busy || h.run.currentIdentity !== 'member'" @click="h.branch('supplement')">补充后重新核实</button>
    </div>
    <template v-else-if="h.currentFlow">
      <div :class="h.canDoCurrent ? 'lab-note' : 'lab-warning'">
        当前：{{ h.currentFlow.name }} · {{ h.currentFlow.actor }}<br>
        {{ h.currentFlow.desc }}<span v-if="!h.identityMatches">请切换至相应身份。</span>
      </div>
      <div v-if="h.selectedCase.status === 'FALSE_REVIEW'" class="lab-warning">网格员已提交“不属实”结论，当前由指挥者复核；复核通过后才能进入结案反馈。</div>
      <template v-if="h.identityMatches">
        <label v-if="h.currentFlow.k === 'verify'" class="lab-field">核实结论
          <select v-model="h.verification">
            <option value="TRUE">属实，继续办理</option>
            <option value="FALSE">不属实，说明原因并提交指挥者复核</option>
            <option value="INSUFFICIENT">资料不足，待补充材料</option>
          </select>
        </label>
        <label v-if="['verify', 'dossier', 'handle', 'close'].includes(h.currentFlow.k)" class="lab-field">{{ h.noteLabels[h.currentFlow.k] }}
          <textarea v-model="h.operationNote" rows="3" :placeholder="h.notePlaceholders[h.currentFlow.k]"></textarea>
        </label>
        <template v-if="h.currentFlow.k === 'dispatch'">
          <label class="lab-field">派遣至职能部门
            <select v-model="h.departmentId">
              <option v-for="department in h.reference.departments" :key="department.id" :value="department.id">{{ department.name }}（{{ department.duty }}）</option>
            </select>
          </label>
          <label class="lab-field">具体承办人员
            <select v-model="h.assignedPersonId">
              <option value="">请选择承办人员</option>
              <option v-for="person in h.dispatchPeople" :key="person.id" :value="person.id">{{ person.name }} · {{ person.roleName || person.role }} · 上限 {{ person.maxTasks || 1 }} 件</option>
            </select>
          </label>
        </template>
        <label v-if="h.currentFlow.k === 'review'" class="lab-field">复核结论
          <select v-model="h.review">
            <option value="合格">合格，同意结案</option>
            <option value="不合格">不合格，退回重办</option>
          </select>
        </label>
        <label v-if="h.currentFlow.k === 'close'" class="lab-field">群众满意度
          <select v-model.number="h.satisfaction">
            <option :value="5">5 分 · 非常满意</option>
            <option :value="4">4 分 · 满意</option>
            <option :value="3">3 分 · 基本满意</option>
            <option :value="2">2 分 · 不满意</option>
            <option :value="1">1 分 · 非常不满意</option>
          </select>
        </label>
        <div class="lab-button-row lab-spaced">
          <button class="btn small primary" :disabled="!h.canDoCurrent || h.busy" @click="h.submitCurrent()">{{ h.actionLabels[h.currentFlow.k] }}</button>
          <button class="btn small" :disabled="h.busy" @click="h.preview()">预览指标变化</button>
        </div>
      </template>
    </template>

    <div v-if="h.canOperate && h.run.currentIdentity === 'commander'" class="lab-button-row lab-spaced">
      <button v-if="h.selectedCase.flowStep >= 4 && h.selectedCase.flowStep < 7 && !h.selectedCase.falseReport" class="btn small" :disabled="h.busy" @click="h.branch('reassign')">改派 / 退回派遣</button>
      <button v-if="h.selectedCase.flowStep === 7" class="btn small" :disabled="h.busy" @click="h.branch('reopen')">撤销结案</button>
    </div>

    <details class="lab-spaced">
      <summary>案件完整信息和操作记录</summary>
      <dl class="lab-kv">
        <dt>影响范围</dt><dd>{{ h.selectedCase.affectedPeople == null ? '—' : h.selectedCase.affectedPeople + ' 人' }}</dd>
        <dt>持续天数</dt><dd>{{ h.selectedCase.durationDays }}</dd>
        <dt>释放规则</dt><dd>{{ h.releaseText(h.selectedCase) }}</dd>
        <dt>处置提示</dt><dd>{{ h.selectedCase.hint || '—' }}</dd>
        <dt>影响公式</dt><dd>{{ h.selectedCase.impactFormula || h.selectedCase.eventFormula || '—' }}</dd>
        <dt>效益参数</dt><dd>{{ h.effectText(h.selectedCase.effects) }}</dd>
        <dt>超时情况</dt><dd>{{ h.selectedCase.overdueMinutes || 0 }} 分钟</dd>
      </dl>
      <div class="lab-timeline">
        <div v-for="(item, index) in h.selectedCase.log || []" :key="index">
          <small>{{ h.formatTime(item.at) }} · {{ item.who }}</small>
          <p>{{ item.act }}{{ item.note ? '：' + item.note : '' }}</p>
        </div>
      </div>
    </details>
  </section>
</template>

<script>
export default {
  name: 'NetworkCaseProcess',
  props: {
    h: { type: Object, required: true }
  }
}
</script>
